import { getCatalogRamSnapshotCache } from '../../catalog/infra-api';
import { supabase } from '../../shared/supabase/supabaseClient';
import { createKeyedTtlCache } from '../../shared/data/keyedTtlCache';
import { registerSessionCacheClearHandler } from '../../shared/ports/sessionResetPort';
import { invalidateHomeSnapshot } from '../../shell/home/homeSnapshotRepo';
import AsyncStorage from '@react-native-async-storage/async-storage';

const day1ProductCountKey = (branchId: string) => `day1.non_misc_count.v1.${branchId}`;

/** Último conteo conocido (sobrevive cold start antes de que la RAM del catálogo hidrate). */
export async function readCachedDay1NonMiscProductCount(branchId: string): Promise<number | null> {
  try {
    const raw = await AsyncStorage.getItem(day1ProductCountKey(branchId));
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  } catch {
    return null;
  }
}

export async function writeCachedDay1NonMiscProductCount(
  branchId: string,
  count: number,
): Promise<void> {
  try {
    await AsyncStorage.setItem(day1ProductCountKey(branchId), String(Math.max(0, Math.floor(count))));
  } catch {
    /* ignore */
  }
}

/** Productos vendibles (excluye Varios / misc). Org-wide. */
export async function readDay1NonMiscProductCount(): Promise<number> {
  const products = getCatalogRamSnapshotCache()?.products ?? [];
        return products.filter(
          (p) => Number(p.is_misc ?? 0) === 0 && Number(p.is_active ?? 1) === 1,
        ).length;
}

/** Vendibles marcados visibles online (`is_public`). Default de producto = público. */
export function countOnlineVisibleProducts(
  products: Array<{
    is_misc?: number | boolean | null;
    is_active?: number | boolean | null;
    is_public?: boolean | null;
  }> | null,
): number | null {
  if (!products) return null;
  return products.filter(
    (p) =>
      Number(p.is_misc ?? 0) === 0 &&
      Number(p.is_active ?? 1) === 1 &&
      (p.is_public ?? true) !== false,
  ).length;
}

/**
 * La primera venta es inmutable una vez existe; si aún no hay, la card semanal
 * exige ≥7 días igual — un null cacheado unos minutos no cambia el render.
 */
const firstSaleIsoCache = createKeyedTtlCache(5 * 60_000);

registerSessionCacheClearHandler(() => firstSaleIsoCache.clear());

/** ISO de la primera venta activa en la sucursal (null si no hay ventas). */
export async function readFirstSaleCreatedAtIso(branchId: string): Promise<string | null> {
  return firstSaleIsoCache.get(branchId, async () => {
    // ADR-017 Fase 4: SQLite local eliminado — leer de Supabase.
    const { data, error } = await supabase.schema('pos')
      .from('sales')
      .select('created_at')
      .eq('branch_id', branchId)
      .or('status.eq.active,status.is.null')
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const v = data?.created_at;
    return v ? String(v) : null;
  });
}

/**
 * True si el catálogo local ya refleja un pull o tiene filas (incl. Varios).
 * Evita tratar “SQLite vacío pre-sync” como dueño fresh.
 *
 * En ramOnly (ADR-017): el timestamp de pull se hidrata desde storage al boot,
 * pero la RAM cache (`getCatalogRamSnapshotCache`) se puebla recién cuando el
 * snapshot sync termina. Si devolvemos `true` con la cache `null`, el conteo de
 * productos da 0 y la card día 1 muestra “Cargar productos” como pendiente
 * aunque el dueño ya tenga catálogo. Requerimos ambas señales.
 */
export async function readDay1CatalogHydrated(): Promise<boolean> {
  return getCatalogRamSnapshotCache() != null;
}

/**
 * El hito día 1 (primera venta / primer turno) acaba de completarse: el TTL de
 * 15s del snapshot podría servir lifetime_* stale al volver a Home y esconder
 * la celebración. Handoff cross-module: pos/appointments invalidan vía acá,
 * sin importar shell/home directo.
 */
export function invalidateDay1Evidence(branchId: string): void {
  invalidateHomeSnapshot(branchId);
}
